import polyline from '@mapbox/polyline';
import qs from 'query-string';

import { proxyApi } from '../../api';
import { SEARCH_AREA } from '../config';
import type { GeoProvider, LatLng, PlaceSuggestion } from '../types';

type AddressComponent = {
  short_name: string;
  long_name: string;
  types: string[];
};

type GeocodeResponse = {
  status: string;
  results: Array<{ formatted_address: string; address_components: AddressComponent[] }>;
};

type DirectionsResponse = {
  status: string;
  routes: Array<{
    legs: Array<{ distance: { value: number } }>;
    overview_polyline: { points: string };
  }>;
};

type AutocompleteResponse = {
  predictions: Array<{ description: string; place_id: string }>;
};

type PlaceDetailsResponse = {
  result: { geometry: { location: LatLng } };
};

// У Directions API нет CORS, поэтому Directions и Places идут через бэкенд-прокси
const proxy = async <T>(url: string): Promise<T> => (await proxyApi.proxy(url)) as T;

export function createGoogleProvider({ apiKey }: { apiKey: string }): GeoProvider {
  return {
    async reverseGeocode(lat: number, lng: number): Promise<string> {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&language=ru&key=${apiKey}`
      );
      if (!response.ok) {
        throw Error('Geocoding server is not availiable');
      }
      const body = (await response.json()) as GeocodeResponse;
      if (body.status !== 'OK') {
        throw new Error(`Server operation status ${body.status}`);
      }
      /**
       * Гугл на предосталяет набор элементов адреса,
       * но нам нужны только улица (фильтруем "Unnamed road"), дом, субъект, город/район субъекта
       *
       * Если ничего из вышеперечисленного у нас нет, то выводим хотя бы страну.
       *
       * Если же и страны у нас нет, то выводим то, что гугл нам предоставил
       * в качестве комбинированной строки
       */
      const addressComponents = body.results[0].address_components.filter(
        (address) => !address.types.includes('postal_code')
      );
      const find = (predicate: (item: AddressComponent) => boolean) =>
        addressComponents.find(predicate);
      const route = find(
        (item) =>
          item.types.includes('route') && item.short_name.toLowerCase() !== 'unnamed road'
      );
      const streetNumber = find((item) => item.types.includes('street_number'));
      const city = find((item) => item.types.includes('administrative_area_level_2'));
      const district = find((item) => item.types.includes('administrative_area_level_1'));
      const country = find((item) => item.types.includes('country'));
      let customAddressComponents = [route, streetNumber, city, district].filter(
        (item): item is AddressComponent => !!item
      );
      if (!customAddressComponents.length && !!country) {
        // Делаем так, потому что в стране short_name вместо "Россия" содержит "RU"
        customAddressComponents = [{ ...country, short_name: country.long_name }];
      }
      if (customAddressComponents.length) {
        return customAddressComponents.map((item) => item.short_name).join(', ');
      }
      return body.results[0].formatted_address;
    },

    // дока https://developers.google.com/maps/documentation/directions/intro
    async route(points: LatLng[]) {
      if (points.length >= 10) {
        throw new Error('Maps platform allows max 10 waypoint');
      }
      const toParam = ({ lat, lng }: LatLng) => `${lat},${lng}`;
      const origin = toParam(points[0]);
      const destination = toParam(points[points.length - 1]);
      // промежуточные точки идут отдельным параметром запроса
      const transitPoints = points.slice(1, -1).map(toParam);
      const waypointsParam = transitPoints.length
        ? `&waypoints=${transitPoints.join('|')}`
        : '';

      const response = await proxy<DirectionsResponse>(
        `https://maps.googleapis.com/maps/api/directions/json?origin=${origin}&destination=${destination}${waypointsParam}&key=${apiKey}`
      );
      if (response.status === 'ZERO_RESULTS') {
        throw new Error('Проверьте доступность подъезда к геокоординатам');
      }
      if (response.status !== 'OK') {
        throw new Error(`Server operation status ${response.status}`);
      }
      const route = response.routes[0];
      return {
        legDistances: route.legs.map((leg) => leg.distance.value),
        // У гугла свой формат для передачи геометрий, используем мапбоксовский декодер
        geoJSON: polyline.toGeoJSON(route.overview_polyline.points),
      };
    },

    async searchPlaces(query: string): Promise<PlaceSuggestion[]> {
      const requestOptions = qs.stringify({
        key: apiKey,
        language: 'ru',
        input: query,
        // Радиус поиска мест
        location: `${SEARCH_AREA.lat},${SEARCH_AREA.lng}`,
        radius: SEARCH_AREA.radiusKm * 1000,
        // Поиск строго в этом радиусе и location
        strictbounds: true,
        // Ограничиваем выборку по России
        components: `country:${SEARCH_AREA.countryCode.toLowerCase()}`,
      });
      const response = await proxy<AutocompleteResponse>(
        `https://maps.googleapis.com/maps/api/place/autocomplete/json?${requestOptions}`
      );
      return response.predictions.map(({ description, place_id }) => ({
        address: description,
        name: description,
        placeId: place_id,
        radius: 1,
      }));
    },

    async resolvePlace(place: PlaceSuggestion): Promise<LatLng> {
      const requestOptions = qs.stringify({
        key: apiKey,
        placeid: place.placeId,
        language: 'ru',
        fields: 'geometry',
      });
      const response = await proxy<PlaceDetailsResponse>(
        `https://maps.googleapis.com/maps/api/place/details/json?${requestOptions}`
      );
      const { lat, lng } = response.result.geometry.location;
      return { lat, lng };
    },
  };
}
